import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Input, Textarea } from '../../components';
import StepContainer from '../../components/onboarding/StepContainer';
import { useOnboardingStore } from '../../stores/onboardingStore';

const websiteSchema = z.object({
  domain: z
    .string()
    .min(1, 'Domain is required')
    .refine((val) => {
      // Must be a valid URL
      try {
        new URL(val.startsWith('http') ? val : `https://${val}`);
        return true;
      } catch {
        return false;
      }
    }, 'Please enter a valid domain (e.g., example.com or https://example.com)'),
  websiteName: z.string().min(2, 'Website name must be at least 2 characters'),
  websiteDescription: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.trim().length >= 20,
      'Description must be at least 20 characters'
    )
    .refine(
      (val) => !val || val.trim().length <= 300,
      'Description must be less than 300 characters'
    ),
});

type WebsiteForm = z.infer<typeof websiteSchema>;

export default function WebsiteCreationStep() {
  const { t } = useTranslation();
  const { websiteData, saveWebsiteData, nextStep, previousStep } = useOnboardingStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<WebsiteForm>({
    resolver: zodResolver(websiteSchema),
    defaultValues: {
      domain: websiteData.domain || '',
      websiteName: websiteData.websiteName || '',
      websiteDescription: websiteData.websiteDescription || '',
    },
  });

  const onSubmit = async (data: WebsiteForm) => {
    // Normalize domain to include https:// if not present
    let normalizedDomain = data.domain.trim();
    if (!normalizedDomain.startsWith('http://') && !normalizedDomain.startsWith('https://')) {
      normalizedDomain = `https://${normalizedDomain}`;
    }

    // Save to store with normalized domain
    saveWebsiteData({
      domain: normalizedDomain,
      websiteName: data.websiteName,
      websiteDescription: data.websiteDescription,
    });

    // Move to next step
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StepContainer
        title={t('onboarding.website.title', 'Create your first website')}
        description={t(
          'onboarding.website.subtitle',
          'Enter your website details to get started'
        )}
        onNext={handleSubmit(onSubmit)}
        onBack={previousStep}
        isLoading={isSubmitting}
      >
        <div className="space-y-6">
          {/* Domain Input */}
          <div>
            <Input
              label={t('onboarding.website.domain', 'Domain') + ' *'}
              type="text"
              placeholder="example.com"
              error={errors.domain?.message}
              {...register('domain')}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'onboarding.website.domainHelper',
                'Enter your website domain (e.g., example.com or https://example.com)'
              )}
            </p>
          </div>

          {/* Website Name Input */}
          <div>
            <Input
              label={t('onboarding.website.name', 'Website Name') + ' *'}
              type="text"
              placeholder="My Awesome Website"
              error={errors.websiteName?.message}
              {...register('websiteName')}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'onboarding.website.nameHelper',
                'A friendly name for your website (e.g., "Main Website", "Blog")'
              )}
            </p>
          </div>

          {/* Website Description */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('onboarding.website.description', 'Description')}
            </label>
            <Textarea
              placeholder={t(
                'onboarding.website.descriptionPlaceholder',
                'Briefly describe your website and what it offers...'
              )}
              rows={4}
              error={errors.websiteDescription?.message}
              {...register('websiteDescription')}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'onboarding.website.descriptionHelper',
                'Optional: Help our AI understand your website better (20-300 characters)'
              )}
            </p>
          </div>

          {/* Info box */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950 border-2 border-blue-600 dark:border-blue-400 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>{t('onboarding.website.infoTitle', 'Multi-domain support')}</strong>
              <br />
              {t(
                'onboarding.website.infoText',
                "You can manage multiple websites from one workspace. We'll start with your first website now."
              )}
            </p>
          </div>
        </div>
      </StepContainer>
    </form>
  );
}
